import React, { useState } from 'react';
import { usePatientDevices } from '../../../state/data/patient';
import { useDeviceOrders } from '../../../state/data/dropshipment';
import { deviceModelName, orderProductLabel } from '../../../logic/devices';
import ShipDeviceModal from '../devices/ShipDeviceModal';
import OrderDetailModal from '../devices/OrderDetailModal';
import OrderStatusBadge, { formatDate } from '../devices/orderUi';
import DeviceGlyph from '../devices/DeviceGlyph';
import EmptyState from '../shared/EmptyState';

// Cells sit on the grey page (--wi-background-primary #f7f5f3) → white (secondary) pops.
const rowCard = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)', borderRadius: 10, flexWrap: 'wrap' };
const rowList = { display: 'flex', flexDirection: 'column', gap: 8 };
const orderRow = {
  ...rowCard, flexWrap: 'nowrap', width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--wi-font-family)',
};
const sectionHeader = { display: 'flex', alignItems: 'center', margin: '4px 0 10px' };

function SectionError({ onRetry }) {
  return (
    <EmptyState
      compact
      title="Couldn't load this section"
      action={<button type="button" className="btn-secondary" onClick={onRetry}>Retry</button>}
    />
  );
}

export default function DevicesTab({ patient }) {
  const [shipOpen, setShipOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const devicesQ = usePatientDevices(patient.userid);
  const ordersQ = useDeviceOrders(patient.userid);

  const devices = devicesQ.data ?? [];
  const orders = ordersQ.data ?? [];

  return (
    <div>
      {/* ── Order CTA (always present) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)', borderRadius: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--wi-text-secondary)' }}><DeviceGlyph size={36} /></span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p className="body2" style={{ margin: '0 0 2px', fontWeight: 600 }}>Order a device</p>
          <p className="detail1 text-secondary" style={{ margin: 0, lineHeight: 1.5 }}>
            Ship a Withings device to the patient. It&apos;s delivered to their address and attaches to their account automatically.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShipOpen(true)}>Order a device</button>
      </div>

      {/* ── Devices on the account ── */}
      <section style={{ marginBottom: 24 }}>
        <div style={sectionHeader}><span className="eyebrow text-secondary">Devices on the account</span></div>
        {devicesQ.isLoading && <div className="skeleton-block" style={{ height: 60, borderRadius: 10 }} />}
        {!devicesQ.isLoading && devicesQ.isError && <SectionError onRetry={() => devicesQ.refetch()} />}
        {!devicesQ.isLoading && !devicesQ.isError && devices.length === 0 && (
          <EmptyState
            compact
            title="No devices attached yet"
            description="A device attaches here automatically once the patient sets it up."
          />
        )}
        {!devicesQ.isLoading && !devicesQ.isError && devices.length > 0 && (
          <div style={rowList}>
            {devices.map((d) => {
              const lastSeen = Math.max(d.lastsessiondate || 0, d.lastuseddate || 0);
              const meta = [d.serialnumber ? `SN ${d.serialnumber}` : '', lastSeen ? `Last sync ${formatDate(lastSeen)}` : ''].filter(Boolean).join(' · ');
              return (
                <div key={d.id} style={rowCard}>
                  <span style={{ color: 'var(--wi-text-secondary)' }}><DeviceGlyph size={28} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="body2" style={{ fontWeight: 600 }}>{deviceModelName(d.model)}</div>
                    {meta && <div className="detail1 text-secondary">{meta}</div>}
                  </div>
                  {d.batterylvl != null && <span className="detail1 text-secondary">Battery {d.batterylvl}%</span>}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Recent orders (each row opens a detail modal) ── */}
      <section>
        <div style={sectionHeader}><span className="eyebrow text-secondary">Recent orders</span></div>
        {ordersQ.isLoading && <div className="skeleton-block" style={{ height: 60, borderRadius: 10 }} />}
        {!ordersQ.isLoading && ordersQ.isError && <SectionError onRetry={() => ordersQ.refetch()} />}
        {!ordersQ.isLoading && !ordersQ.isError && orders.length === 0 && (
          <EmptyState compact title="No orders yet" />
        )}
        {!ordersQ.isLoading && !ordersQ.isError && orders.length > 0 && (
          <div style={rowList}>
            {orders.map((o) => {
              const productNames = (o.products ?? []).map((p) => orderProductLabel(p)).join(', ');
              const meta = [o.order_reference ? `Ref ${o.order_reference}` : '', o.created ? formatDate(o.created) : ''].filter(Boolean).join(' · ');
              return (
                <button
                  key={o.order_reference || o.created}
                  type="button"
                  style={orderRow}
                  onClick={() => setSelectedOrder(o)}
                  aria-label={`View order ${o.order_reference || ''} details`}
                >
                  <OrderStatusBadge status={o.status} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="body2" style={{ fontWeight: 600 }}>{productNames || 'Device order'}</div>
                    {meta && <div className="detail1 text-secondary">{meta}</div>}
                  </div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--wi-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {shipOpen && (
        <ShipDeviceModal key={patient.userid} patient={patient} onClose={() => setShipOpen(false)} />
      )}
      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}
